import { useRouter } from "vue-router";
import { mapGetters } from "vuex";
import {
	XMarkIcon,
	LinkIcon,
	CodeBracketIcon,
	ClipboardDocumentIcon,
	CheckIcon,
	ShareIcon,
} from "@heroicons/vue/24/outline";

export default {
	name: "ShareModal",
	components: {
		XMarkIcon,
		LinkIcon,
		CodeBracketIcon,
		ClipboardDocumentIcon,
		CheckIcon,
		ShareIcon,
	},
	props: ["toggleShareModal", "isVisible"],
	computed: {
		...mapGetters("staticContent", ["getTranslations"]),
		shareUrl() {
			const baseUrl = window.location.origin;
			const currentPath = this.router.currentRoute.value.fullPath;
			return `${baseUrl}${currentPath}`;
		},
		embedCode() {
			const url = this.shareUrl;

			if (this.embed_type === "responsive") {
				const padding_bottom = (
					(this.selected_aspect_ratio.height /
						this.selected_aspect_ratio.width) *
					100
				).toFixed(2);
				return `<div style="position: relative; width: 100%; height: 0; padding-bottom: ${padding_bottom}%;">
  <iframe src="${url}" 
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" 
          frameborder="0" 
          allowfullscreen>
  </iframe>
</div>`;
			} else {
				return `<iframe src="${url}" width="${this.iframe_width}" height="${this.iframe_height}" frameborder="0" allowfullscreen></iframe>`;
			}
		},
		isNativeShareSupported() {
			return navigator.share !== undefined;
		},
		modalHeight() {
			if (this.active_tab === "link") {
				return this.isNativeShareSupported ? 380 : 320;
			} else {
				return this.embed_type === "fixed" ? 620 : 520;
			}
		},
		contentHeight() {
			if (this.active_tab === "link") {
				return this.isNativeShareSupported ? 160 : 100;
			} else {
				return this.embed_type === "fixed" ? 420 : 320;
			}
		},
	},
	watch: {
		isVisible(newVal) {
			const event = new CustomEvent("modal-state-change", {
				detail: { isOpen: newVal },
			});
			document.dispatchEvent(event);
		},
	},
	methods: {
		async copyToClipboard(text, type) {
			try {
				await navigator.clipboard.writeText(text);
				if (type === "link") {
					this.link_copied = true;
					setTimeout(() => {
						this.link_copied = false;
					}, 2000);
				} else if (type === "embed") {
					this.embed_copied = true;
					setTimeout(() => {
						this.embed_copied = false;
					}, 2000);
				}
			} catch (err) {
				console.error("Failed to copy to clipboard:", err);
				this.fallbackCopy(text);
			}
		},
		fallbackCopy(text) {
			const text_area = document.createElement("textarea");
			text_area.value = text;
			text_area.style.position = "fixed";
			text_area.style.left = "-999999px";
			text_area.style.top = "-999999px";
			document.body.appendChild(text_area);
			text_area.focus();
			text_area.select();

			try {
				document.execCommand("copy");
				this.link_copied = true;
				setTimeout(() => {
					this.link_copied = false;
				}, 2000);
			} catch (err) {
				console.error("Fallback copy failed:", err);
			}

			document.body.removeChild(text_area);
		},
		async shareNative() {
			if (navigator.share) {
				try {
					await navigator.share({
						title: document.title,
						url: this.shareUrl,
					});
				} catch (err) {
					if (err.name !== "AbortError") {
						console.error("Native sharing failed:", err);
					}
				}
			}
		},
		setPresetSize(preset) {
			this.iframe_width = preset.width;
			this.iframe_height = preset.height;
		},
		dispatchModalStateChange(is_open) {
			const event = new CustomEvent("modal-state-change", {
				detail: { isOpen: is_open },
			});
			document.dispatchEvent(event);
		},
		lockBodyScroll() {
			this.scrollY = window.scrollY;

			document.body.style.position = "fixed";
			document.body.style.top = `-${this.scrollY}px`;
			document.body.style.left = "0";
			document.body.style.right = "0";
			document.body.style.overflow = "hidden";
		},
		unlockBodyScroll() {
			document.body.style.position = "";
			document.body.style.top = "";
			document.body.style.left = "";
			document.body.style.right = "";
			document.body.style.overflow = "";

			if (this.scrollY !== undefined) {
				window.scrollTo(0, this.scrollY);
			}
		},
	},
	data() {
		return {
			active_tab: "link",
			link_copied: false,
			embed_copied: false,
			scroll_y: 0,
			embed_type: "responsive",
			iframe_width: 800,
			iframe_height: 600,
			selected_aspect_ratio: { name: "16:9", width: 16, height: 9 },
			aspect_ratios: [
				{ name: "16:9", width: 16, height: 9 },
				{ name: "4:3", width: 4, height: 3 },
				{ name: "1:1", width: 1, height: 1 },
				{ name: "21:9", width: 21, height: 9 },
				{ name: "3:2", width: 3, height: 2 },
				{ name: "9:16", width: 9, height: 16 },
			],
			size_presets: [
				{ name: "Mobile", width: 375, height: 667 },
				{ name: "Tablet", width: 768, height: 1024 },
				{ name: "Desktop", width: 1440, height: 900 },
			],
		};
	},
	mounted() {
		// Remove the direct lockBodyScroll call since it's now handled by the watch
	},
	beforeUnmount() {
		document.body.classList.remove("overflow-hidden", "fixed", "w-full");
		document.body.style.top = "";
	},
	setup() {
		const router = useRouter();
		return { router };
	},
};
